import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@creditify/shared-fe/stores/auth'

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      redirect: '/dashboard',
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('./pages/LoginPage.vue'),
      meta: { guest: true },
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('./pages/RegisterPage.vue'),
      meta: { guest: true },
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import('./pages/DashboardPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/workspaces',
      name: 'workspaces',
      component: () => import('./pages/WorkspaceListPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/workspaces/new',
      name: 'workspaces-new',
      component: () => import('./pages/WorkspaceCreatePage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/workspaces/:id/members',
      name: 'workspace-members',
      component: () => import('./pages/WorkspaceMembersPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/invitations/:token',
      name: 'invitation',
      component: () => import('./pages/InvitationPage.vue'),
    },
    {
      path: '/settings',
      name: 'settings',
      component: () => import('./pages/SettingsPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/budget',
      name: 'budget',
      component: () => import('./pages/BudgetPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/bills',
      name: 'bills',
      component: () => import('./pages/BillsPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/statements',
      name: 'statements',
      component: () => import('./pages/StatementsPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/statements/rules',
      name: 'statements-rules',
      component: () => import('./pages/StatementRulesPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/insights',
      name: 'insights',
      component: () => import('./pages/InsightsPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/legal/:slug',
      name: 'legal',
      component: () => import('./pages/LegalPage.vue'),
      // No requiresAuth — public legal disclosures must be reachable
      // without an account.
    },
    {
      path: '/savings',
      name: 'savings',
      component: () => import('./pages/SavingsPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/investments',
      name: 'investments',
      component: () => import('./pages/InvestmentsPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      // Throwaway dev surface for the Flexoki/shadcn-vue foundation.
      path: '/_kitchen-sink',
      name: 'kitchen-sink',
      component: () => import('./pages/KitchenSinkPage.vue'),
    },
    {
      path: '/status',
      name: 'status',
      component: () => import('./pages/StatusPage.vue'),
      // No requiresAuth — public service status, like
      // status.creditify.app for any visitor.
    },
    {
      path: '/business',
      name: 'business-dashboard',
      component: () => import('./pages/business/BusinessDashboardPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/business/invoices',
      name: 'business-invoices',
      component: () => import('./pages/business/BusinessInvoicesPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/business/invoices/new',
      name: 'business-invoice-new',
      component: () => import('./pages/business/BusinessInvoiceFormPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/business/invoices/:id/edit',
      name: 'business-invoice-edit',
      component: () => import('./pages/business/BusinessInvoiceFormPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/business/invoices/:id',
      name: 'business-invoice-detail',
      component: () => import('./pages/business/BusinessInvoiceDetailPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/business/counterparties',
      name: 'business-counterparties',
      component: () => import('./pages/business/BusinessCounterpartiesPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/business/vat',
      name: 'business-vat',
      component: () => import('./pages/business/BusinessVatPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/business/recurring',
      name: 'business-recurring',
      component: () => import('./pages/business/BusinessRecurringPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/business/payroll',
      name: 'business-payroll',
      component: () => import('./pages/business/BusinessPayrollPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/business/employees',
      name: 'business-employees',
      component: () => import('./pages/business/BusinessEmployeesPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/business/employees/:id',
      name: 'business-employee-detail',
      component: () => import('./pages/business/BusinessEmployeeDetailPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/business/bank',
      name: 'business-bank',
      component: () => import('./pages/business/BusinessBankPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/business/reconciliation',
      name: 'business-reconciliation',
      component: () => import('./pages/business/BusinessReconciliationPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/business/contracts',
      name: 'business-contracts',
      component: () => import('./pages/business/BusinessContractsPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/business/timesheets',
      name: 'business-timesheets',
      component: () => import('./pages/business/BusinessTimesheetsPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/business/services',
      name: 'business-services',
      component: () => import('./pages/business/BusinessServicesPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/business/tax-rates',
      name: 'business-tax-rates',
      component: () => import('./pages/business/BusinessTaxRatesPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/business/reports',
      name: 'business-reports',
      component: () => import('./pages/business/BusinessReportsPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/business/parse',
      name: 'business-parse',
      component: () => import('./pages/business/BusinessParsePage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/admin/audit',
      name: 'admin-audit',
      component: () => import('./pages/admin/AuditPage.vue'),
      meta: { requiresAuth: true, requiresPlatformAdmin: true },
    },
    {
      path: '/admin/suspensions',
      name: 'admin-suspensions',
      component: () => import('./pages/admin/SuspensionsPage.vue'),
      meta: { requiresAuth: true, requiresPlatformAdmin: true },
    },
    {
      path: '/admin/tenants',
      name: 'admin-tenants',
      component: () => import('./pages/admin/TenantsPage.vue'),
      meta: { requiresAuth: true, requiresPlatformAdmin: true },
    },
  ],
})

router.beforeEach(async (to: RouteLocationNormalized) => {
  const auth = useAuthStore()
  if (!auth.ready) {
    await auth.initialize()
  }
  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }
  if (to.meta.guest && auth.isAuthenticated) {
    return { name: 'dashboard' }
  }
  if (to.meta.requiresPlatformAdmin && !auth.user?.isPlatformAdmin) {
    return { name: 'dashboard' }
  }
  return true
})
